import {
  Badge,
  Box,
  Button,
  Flex,
  Link,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  formatDollars,
  formatJsDate,
  formatPeriodShort,
  statusColor,
} from '~/backup/shared/format';
import {
  PortalBusServicingCyclesForDealQueryGQLType,
  usePortalBusServicingCycleLazyQueryGQLType,
  usePortalBusServicingCyclesForDealQuery,
} from '~/backup/shared/views/deal-overview/dealOverview.gql.generated';

type DealCycle = PortalBusServicingCyclesForDealQueryGQLType['portalBusServicingCycles'][number];

/**
 * Downloads the certificate PDF for a single cycle. The signed URL is not on the
 * list row (it is minted per request), so we lazily fetch the cycle detail on
 * click and open the URL — or toast when it has not rendered yet. Tape / MSR
 * downloads are deferred (no signed-URL surface for those artifact refs yet).
 */
function CertDownloadButton({
  servicingCycleId,
}: {
  servicingCycleId: string;
}): React.ReactElement {
  const toast = useToast();
  const [fetchCycle, { loading }] = usePortalBusServicingCycleLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const url = data.portalBusServicingCycle?.certificateDownloadUrl ?? null;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast({
          title: 'Certificate not ready',
          description: 'The certificate PDF has not finished rendering yet.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    onError: () => {
      toast({
        title: 'Could not fetch certificate',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });

  return (
    <Button
      size="xs"
      variant="link"
      colorScheme="blue"
      isLoading={loading}
      onClick={() => fetchCycle({ variables: { servicingCycleId } })}
    >
      Cert PDF
    </Button>
  );
}

function ValidationCell({
  status,
}: {
  status: string | null | undefined;
}): React.ReactElement {
  const label = (status ?? 'pending').toUpperCase();
  return (
    <Text fontWeight={600} fontSize="0.8rem" color={statusColor(status ?? '')}>
      {label}
    </Text>
  );
}

// -- component --

export const FilesView = (): React.ReactElement => {
  const { implementationId } = useParams<{ implementationId: string }>();

  const { data, loading, error } = usePortalBusServicingCyclesForDealQuery({
    variables: { implementationId: implementationId ?? '' },
    skip: !implementationId,
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Flex justify="center" align="center" minH="20rem">
        <Spinner />
      </Flex>
    );
  }

  const cycles: DealCycle[] = data?.portalBusServicingCycles ?? [];

  if (error) {
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          Something went wrong
        </Typography>
        <Text color={colors.gray[500]}>
          Could not load deliverables. Please try again or contact support.
        </Text>
      </Flex>
    );
  }

  if (cycles.length === 0) {
    return (
      <Flex direction="column" padding="2.5rem" gap="0.5rem">
        <Typography variant="page-title" trim>
          No deliverables
        </Typography>
        <Text color={colors.gray[500]}>
          This deal has no servicing cycles available on your account.
        </Text>
      </Flex>
    );
  }

  const deal = cycles[0];
  const certifiedCount = cycles.filter((c) => c.certificateSignedAt !== null)
    .length;

  return (
    <Flex
      direction="column"
      padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      gap="1.5rem"
    >
      {/* Header */}
      <Box>
        <Flex align="center" gap="0.5rem" mb="0.25rem">
          <Link
            as={RouterLink}
            to={`/deals/${implementationId ?? ''}`}
            fontSize="0.8rem"
            color={colors.gray[500]}
          >
            {deal.creditFacilityName}
          </Link>
          <Text color={colors.gray[300]} fontSize="0.8rem">
            /
          </Text>
          <Text fontSize="0.8rem" color={colors.gray[500]}>
            Deliverables
          </Text>
        </Flex>
        <Typography variant="page-title" trim>
          Monthly Deliverables
        </Typography>
        <Text color={colors.gray[500]} fontSize="0.9rem">
          {certifiedCount} of {cycles.length} cycle
          {cycles.length === 1 ? '' : 's'} certified
        </Text>
      </Box>

      {/* Table */}
      <TableContainer
        borderWidth="1px"
        borderColor={colors.gray[100]}
        borderRadius="md"
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Period</Th>
              <Th>Tape</Th>
              <Th>Validation</Th>
              <Th>Certificate</Th>
              <Th>MSR</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cycles.map((c, i) => (
              <Tr key={c.id}>
                <Td fontWeight={600}>
                  <Flex align="center" gap="0.4rem">
                    {formatPeriodShort(c.reportingPeriod)}
                    {i === 0 && (
                      <Badge colorScheme="blue" fontSize="0.6rem">
                        Latest
                      </Badge>
                    )}
                  </Flex>
                </Td>
                <Td>
                  <Text fontSize="0.8rem">
                    {formatJsDate(c.tapeReceivedAt)}
                  </Text>
                  <Text fontSize="0.75rem" color={colors.gray[500]}>
                    {(c.tapeRowCount ?? 0).toLocaleString()} rows ·{' '}
                    {formatDollars(c.tapeTotalPrincipal)}
                  </Text>
                </Td>
                <Td>
                  <ValidationCell status={c.reconcileStatus} />
                </Td>
                <Td>
                  <Text fontSize="0.8rem">
                    {c.certificateStatus
                      ? c.certificateStatus.toUpperCase()
                      : '—'}
                  </Text>
                  {c.certificateSignedAt !== null && (
                    <Text fontSize="0.75rem" color={colors.gray[500]}>
                      Signed {formatJsDate(c.certificateSignedAt)}
                    </Text>
                  )}
                </Td>
                <Td>
                  <Text fontSize="0.8rem" color={colors.gray[500]}>
                    {formatJsDate(c.msrReceivedAt)}
                  </Text>
                </Td>
                <Td>
                  {c.certificateStatus ? (
                    <CertDownloadButton servicingCycleId={c.id} />
                  ) : (
                    <Text fontSize="0.8rem" color={colors.gray[400]}>
                      —
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Text color={colors.gray[500]} fontSize="0.75rem">
        Source tapes and MSR files are archived per the servicing agreement.
        Direct tape / MSR downloads are coming soon.
      </Text>
    </Flex>
  );
};
